import React, { useRef, useState, ChangeEvent, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  SystemStyleObject,
  FormControl,
  FormErrorMessage,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Tooltip,
  CloseButton,
  FormErrorIcon,
} from '@chakra-ui/react';
import { AuditLogsTable } from './AuditLogsTable';
import { PortalIcon } from 'features/common/components/PortalIcon';
import { mdiMagnify } from '@mdi/js';
import {
  AUDIT_LOGS_SUPPORTED_SYSTEMS,
  AuditLogsSupportedSystemName,
} from './constants';
import { chakraComponents, Select as ReactSelect } from 'chakra-react-select';
import { CustomOption } from '../common/components/TenantsByProductOptions/SelectedProductRow';
import EmptyState from '../common/components/EmptyState';
import { RepeatIcon } from '@chakra-ui/icons';

interface SystemSelectorProps {
  onSystemChange: (system: AuditLogsSupportedSystemName) => void;
}

const SystemSelector = ({ onSystemChange }: SystemSelectorProps) => (
  <Box data-testid='system-select-wrapper' w='100%'>
    <ReactSelect
      placeholder='Select Product'
      maxMenuHeight={274}
      components={{ ...chakraComponents, Option: CustomOption }}
      selectedOptionColorScheme='primary'
      chakraStyles={{
        menuList: (provided: SystemStyleObject) => ({
          ...provided,
          p: 0,
        }),
      }}
      options={AUDIT_LOGS_SUPPORTED_SYSTEMS.map(({ name, label }) => ({
        value: name,
        label,
      }))}
      onChange={(data) =>
        onSystemChange((data as { value: AuditLogsSupportedSystemName }).value)
      }
    />
  </Box>
);

export const AuditLogs = () => {
  const [selectedSystemId, setSelectedSystemId] =
    useState<AuditLogsSupportedSystemName>();
  const tableRef = useRef<{ reloadData: () => void }>(null);
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [searchStringIsValid, setSearchStringIsValid] = useState<boolean>(true);
  function useDebounce(value: string): string {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
      if (process.env.NODE_ENV === 'test') {
        setDebounced(value);
        return;
      }
      const timer = setTimeout(() => setDebounced(value), 400);
      return () => clearTimeout(timer);
    }, [value]);
    return debounced;
  }
  const debouncedSearch = useDebounce(searchFilter);

  const handleSystemChange = (system: AuditLogsSupportedSystemName) => {
    setSelectedSystemId(system);
  };

  return (
    <Flex flexDir='column' height='100%'>
      <Flex justifyContent='space-between' width='100%'>
        <Flex direction='column' alignSelf='start'>
          <Heading as='h1' data-testid='auditLogs-heading'>
            Audit logs
          </Heading>
          <Text maxW='xl' my='8' data-testid='auditLogs-description'>
            Audit logs show trace list of all activities performed across all
            products.
          </Text>
        </Flex>
      </Flex>
      <Flex direction='column' gap={2} mb={4} width='100%'>
        <Flex
          direction={{ base: 'column', lg: 'row' }}
          align={{ base: 'stretch', lg: 'center' }}
          gap={2}
          width='100%'
          flexWrap='wrap'
        >
          <Box w={{ base: '100%', lg: '30%' }} flexShrink={0}>
            <SystemSelector onSystemChange={handleSystemChange} />
          </Box>
          <Box w={{ base: '100%', lg: '30%' }} flexShrink={0}>
            <FormControl isInvalid={!searchStringIsValid}>
              <InputGroup w='100%'>
                <InputLeftElement pointerEvents='none'>
                  <PortalIcon path={mdiMagnify} />
                </InputLeftElement>
                <Input
                  placeholder='Search logs'
                  value={searchFilter}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const val = e.target.value;
                    if (/^[\w@.-]*$/.test(val)) {
                      setSearchStringIsValid(true);
                      setSearchFilter(val);
                      if (
                        !selectedSystemId &&
                        AUDIT_LOGS_SUPPORTED_SYSTEMS.length
                      ) {
                        setSelectedSystemId(
                          AUDIT_LOGS_SUPPORTED_SYSTEMS[0].name,
                        );
                      }
                    } else {
                      setSearchStringIsValid(false);
                    }
                  }}
                  width='100%'
                />
                {searchFilter && (
                  <InputRightElement>
                    <Tooltip label='Clear'>
                      <CloseButton
                        aria-label='Clear'
                        onClick={() => setSearchFilter('')}
                      />
                    </Tooltip>
                  </InputRightElement>
                )}
              </InputGroup>
              <FormErrorMessage
                visibility={searchStringIsValid ? 'hidden' : 'visible'}
                minH='1.25rem'
              >
                <FormErrorIcon />
                Currently, the search is only available in alphanumeric
                characters, @, ., -, _.
              </FormErrorMessage>
            </FormControl>
          </Box>
          {selectedSystemId && (
            <Box
              alignSelf={{ base: 'stretch', lg: 'center' }}
              w={{ base: '100%', lg: 'auto' }}
              ml={{ base: 0, lg: 'auto' }}
              flexShrink={0}
            >
              <Button
                variant='outline'
                aria-label='audit-logs-reload-button'
                size='sm'
                data-testid='audit-logs-reload-button'
                leftIcon={<RepeatIcon />}
                onClick={() => tableRef?.current?.reloadData()}
                w={{ base: '100%', lg: 'auto' }}
                whiteSpace='nowrap'
              >
                Reload data
              </Button>
            </Box>
          )}
        </Flex>
      </Flex>
      {selectedSystemId && (
        <Box
          w='100%'
          textAlign='center'
          mb={4}
          data-testid='selected-system-label-wrapper'
        >
          <Text
            fontSize='sm'
            fontWeight='bold'
            color='chakra-subtle-text'
            data-testid='selected-system-label'
          >
            Selected product:{' '}
            {
              AUDIT_LOGS_SUPPORTED_SYSTEMS.find(
                ({ name }) => name === selectedSystemId,
              )?.label
            }
          </Text>
        </Box>
      )}
      {selectedSystemId ? (
        <AuditLogsTable
          system={selectedSystemId}
          ref={tableRef}
          query={debouncedSearch}
        />
      ) : (
        <Flex flex={1} justifyContent='center' alignItems='center'>
          <EmptyState
            text='No product selected'
            description='Select a product to view its audit logs'
            dataTestId='no-product-selected'
          />
        </Flex>
      )}
    </Flex>
  );
};
